import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format, parseISO } from 'date-fns'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { usePosId } from '../../hooks/usePosId'
import { createPosClosing, getPosClosings, previewPosClosing } from '../../service/pos-closings.service'
import { PosClosingDto, PosClosingPreviewDto, PosClosingVatSummaryDto } from '../../service/types'

interface ClosingSummary {
  posId: string
  totalCash: number
  totalCard: number
  totalInvoice: number
  receiptCount: number
  vatSummaries: PosClosingVatSummaryDto[]
}

export const PosClosings: FC = () => {
  const { t } = useTranslation()
  const { posId } = usePosId()
  const [closings, setClosings] = useState<PosClosingDto[]>([])
  const [loading, setLoading] = useState(false)
  const [previewData, setPreviewData] = useState<PosClosingPreviewDto | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedClosing, setSelectedClosing] = useState<PosClosingDto | null>(null)
  const [pendingReceiptCount, setPendingReceiptCount] = useState<number | null>(null)

  const loadClosings = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getPosClosings()
      setClosings(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  const loadPendingCount = useCallback(async () => {
    if (!posId) return
    try {
      const response = await previewPosClosing({ posId })
      setPendingReceiptCount(response.data.receiptCount)
    } catch {
      setPendingReceiptCount(null)
    }
  }, [posId])

  useEffect(() => {
    void loadClosings()
    void loadPendingCount()
  }, [loadClosings, loadPendingCount])

  const handleOpenPreview = async () => {
    if (!posId) return
    setPreviewLoading(true)
    try {
      const response = await previewPosClosing({ posId })
      setPreviewData(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleConfirmClose = async () => {
    if (!posId) return
    setCreating(true)
    try {
      await createPosClosing({ posId })
      toast.success(t('posClosings.closingCreated'))
      setPreviewData(null)
      await loadClosings()
      await loadPendingCount()
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setCreating(false)
    }
  }

  const formatPrice = (value: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm')
    } catch {
      return dateStr
    }
  }

  const renderSummaryContent = (data: ClosingSummary) => (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('posClosings.paymentBreakdown')}
      </Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableBody>
          <TableRow>
            <TableCell>{t('posClosings.cash')}</TableCell>
            <TableCell align="right">{formatPrice(data.totalCash)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('posClosings.card')}</TableCell>
            <TableCell align="right">{formatPrice(data.totalCard)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('posClosings.invoice')}</TableCell>
            <TableCell align="right">{formatPrice(data.totalInvoice)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('posClosings.total')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatPrice(data.totalCash + data.totalCard + data.totalInvoice)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {data.vatSummaries.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('posClosings.vatBreakdown')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('posClosings.vatRate')}</TableCell>
                <TableCell align="right">{t('posClosings.basePrice')}</TableCell>
                <TableCell align="right">{t('posClosings.vat')}</TableCell>
                <TableCell align="right">{t('posClosings.totalPrice')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.vatSummaries.map((s, i) => (
                <TableRow key={s.id || i}>
                  <TableCell>{s.vatRate}%</TableCell>
                  <TableCell align="right">{formatPrice(s.totalBasePrice)}</TableCell>
                  <TableCell align="right">{formatPrice(s.totalVat)}</TableCell>
                  <TableCell align="right">{formatPrice(s.totalPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </>
  )

  return (
    <>
      <ScreenWrapper
        title={t('posClosings.title')}
        filter={
          <Button
            variant="contained"
            startIcon={<PointOfSaleIcon />}
            onClick={handleOpenPreview}
            disabled={!posId || previewLoading || pendingReceiptCount === 0}
          >
            {t('posClosings.closePOS')}
          </Button>
        }
      >
        {(loading || previewLoading) && <LinearProgress sx={{ mt: 2 }} />}
        {!posId && (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">{t('posClosings.noPosId')}</Typography>
          </Box>
        )}
        <TableContainer component={Paper} sx={{ mt: loading || previewLoading ? 0 : 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('posClosings.date')}</TableCell>
                <TableCell>{t('posClosings.posId')}</TableCell>
                <TableCell align="right">{t('posClosings.receipts')}</TableCell>
                <TableCell align="right">{t('posClosings.cash')}</TableCell>
                <TableCell align="right">{t('posClosings.card')}</TableCell>
                <TableCell align="right">{t('posClosings.invoice')}</TableCell>
                <TableCell align="right">{t('posClosings.total')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && closings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      {t('posClosings.noClosings')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {closings.map((closing) => (
                <TableRow key={closing.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedClosing(closing)}>
                  <TableCell>{closing.closingNumber}</TableCell>
                  <TableCell>{formatDate(closing.createdAt)}</TableCell>
                  <TableCell>
                    <Chip label={closing.posId} size="small" />
                  </TableCell>
                  <TableCell align="right">{closing.receiptCount}</TableCell>
                  <TableCell align="right">{formatPrice(closing.totalCash)}</TableCell>
                  <TableCell align="right">{formatPrice(closing.totalCard)}</TableCell>
                  <TableCell align="right">{formatPrice(closing.totalInvoice)}</TableCell>
                  <TableCell align="right">
                    {formatPrice(closing.totalCash + closing.totalCard + closing.totalInvoice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ScreenWrapper>

      {/* Preview modal — shows before confirming */}
      <Dialog open={!!previewData} onClose={() => setPreviewData(null)} maxWidth="sm" fullWidth>
        {previewData && (
          <>
            <DialogTitle>{t('posClosings.closePOS')}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {previewData.posId} · {previewData.receiptCount} {t('posClosings.receipts').toLowerCase()}
              </Typography>
              {renderSummaryContent(previewData)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewData(null)}>{t('general.button.cancel')}</Button>
              <Button variant="contained" onClick={handleConfirmClose} disabled={creating}>
                {t('posClosings.confirmClose')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Detail modal — read-only view of past closing */}
      <Dialog open={!!selectedClosing} onClose={() => setSelectedClosing(null)} maxWidth="sm" fullWidth>
        {selectedClosing && (
          <>
            <DialogTitle>{t('posClosings.closingDetail', { number: selectedClosing.closingNumber })}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(selectedClosing.createdAt)} · {selectedClosing.posId} · {selectedClosing.receiptCount}{' '}
                {t('posClosings.receipts').toLowerCase()}
              </Typography>
              {renderSummaryContent(selectedClosing)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedClosing(null)}>{t('general.button.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}
